import { UiAction, UiActionStore } from 'src/app/dynamic-ui/models/ui-action.models';
import { UiComponent } from 'src/app/dynamic-ui/models/ui-component.models';
import { LocalStoreEntry } from 'src/app/dynamic-ui/store/dynamic.state';

export function AddUiDefinition(store: LocalStoreEntry, UiDefinitionName: string, storeDataChildNames: string[]): LocalStoreEntry {
    // create a deep copy to avoid mutating the original store
    const newStore: LocalStoreEntry = JSON.parse(JSON.stringify(store || { UiDefValues: {}, UiDefChilds: {} }));

    if (!newStore.UiDefChilds) newStore.UiDefChilds = {};

    let current: Record<string, LocalStoreEntry> = newStore.UiDefChilds;

    // If no child path provided, add at root
    if (!storeDataChildNames || storeDataChildNames.length === 0) {
        current[UiDefinitionName] = {
            UiDefValues: {},
            UiDefChilds: {}
        };
        return newStore;
    }

    // Walk/ensure the path of child names exists
    for (let i = 0; i < storeDataChildNames.length; i++) {
        const key = storeDataChildNames[i];
        if (!current[key]) {
            current[key] = {
                UiDefValues: {},
                UiDefChilds: {}
            };
        } else if (!current[key].UiDefChilds) {
            current[key].UiDefChilds = {};
        }

        // descend
        current = current[key].UiDefChilds as Record<string, LocalStoreEntry>;
    }

    // add the new ui definition at the deepest level
    current[UiDefinitionName] = {
        UiDefValues: {},
        UiDefChilds: {}
    };

    return newStore;
}

/** @deprecated */
export function UpdateDataInArray(
    dataStore: Record<string, any>,
    storeDataName: string,
    storeDataPath: string,
    key: string,
    keyValue: string,
    changeKey: string,
    changeValue: any
): Record<string, any> {
    Object.keys(dataStore)?.forEach((key) => {
        if (key.toLowerCase() !== storeDataName.toLowerCase())
            return { ...dataStore[key] };

        if (storeDataPath === '.' || storeDataPath === '') {
            return {
                ...dataStore[key],
                value: dataStore[key].value.map((entry: any) => {
                    if (
                        entry[key] &&
                        entry[key].toString().toLowerCase() ===
                        keyValue.toString().toLowerCase()
                    ) {
                        return {
                            ...entry,
                            [changeKey]: changeValue,
                        };
                    }
                    return entry;
                }),
            };
        } else {
            if (storeDataPath.startsWith('.'))
                storeDataPath = storeDataPath.slice(1);
            return {
                ...dataStore[key],
                value: dataStore[key].value.map((entry: any) => {
                    if (
                        entry[key] &&
                        entry[key].toString().toLowerCase() ===
                        keyValue.toString().toLowerCase()
                    ) {
                        return {
                            ...entry,
                            [changeKey]: changeValue,
                        };
                    }
                    return entry;
                }),
            };
        }
    });
    return dataStore;
}

export function UpdateArrayInLocalStore(
    obj: LocalStoreEntry,
    pathString: string,
    key: string,
    keyValue: any,
    changeKey: string,
    changeValue: any,
    storeDataChildNames: string[]
): LocalStoreEntry {
    if (!obj) return obj;

    const pathStrings = (pathString || '').split('.').filter(p => p);
    if (pathStrings.length === 0) return obj;

    const firstKey = pathStrings[0];

    // deep copy
    const newObj: LocalStoreEntry = JSON.parse(JSON.stringify(obj));

    // helper to update array at node (immutably)
    const operateOnNode = (node: LocalStoreEntry): boolean => {
        if (!node) return false;
        if (!node.UiDefValues) node.UiDefValues = {};

        const arr: any[] = Array.isArray(node.UiDefValues[firstKey]) ? node.UiDefValues[firstKey] : [];

        // add when keyValue is null and changeKey=='.' and changeValue != null
        if (keyValue === null) {
            if (changeKey === '.' && changeValue !== null) {
                node.UiDefValues[firstKey] = [...arr, changeValue];
                return true;
            }
            return false;
        }

        // otherwise find and update/delete/replace matching entry
        const newArr: any[] = arr.map((entry: any) => ({ ...entry }));

        let replaced = false;
        for (let i = 0; i < newArr.length; i++) {
            const entry = newArr[i];
            if (entry === null || entry === undefined) continue;

            if (!Object.prototype.hasOwnProperty.call(entry, key)) continue;

            const entryVal = entry[key];
            if (entryVal === null || entryVal === undefined) continue;

            // compare as string (case-insensitive) when possible, else strict equality
            let isMatch = false;
            try {
                if (entryVal !== undefined && entryVal !== null && keyValue !== undefined && keyValue !== null) {
                    isMatch = entryVal.toString().toLowerCase() === keyValue.toString().toLowerCase();
                }
            } catch (e) {
                isMatch = entryVal === keyValue;
            }

            if (isMatch) {
                replaced = true;
                if (changeKey === '.') {
                    // replace whole item or remove if changeValue is null
                    if (changeValue === null) {
                        // remove by marking undefined; we'll filter later
                        newArr[i] = undefined as any;
                    } else {
                        newArr[i] = changeValue;
                    }
                } else {
                    newArr[i] = { ...entry, [changeKey]: changeValue };
                }
            }
        }

        // filter out removed entries
        node.UiDefValues[firstKey] = newArr.filter((e) => e !== undefined);
        return replaced;
    };

    // Try to follow explicit child path if provided
    let current: any = newObj;
    let matchedChild = false;

    if (storeDataChildNames && storeDataChildNames.length > 0) {
        for (let i = 0; i < storeDataChildNames.length; i++) {
            const childName = storeDataChildNames[i];
            if (!current || !current.UiDefChilds) break;

            const matchingChildKey = Object.keys(current.UiDefChilds).find(
                (k) => k.toLowerCase() === (childName || '').toLowerCase()
            );

            if (!matchingChildKey) break;

            current = current.UiDefChilds[matchingChildKey];
            if (!current) break;

            matchedChild = true;
        }
    }

    // Helper: depth-first search for first node under `node` that contains the array key
    const findNodeWithKey = (node: LocalStoreEntry): LocalStoreEntry | undefined => {
        if (!node) return undefined;
        if (node.UiDefValues && Object.prototype.hasOwnProperty.call(node.UiDefValues, firstKey)) return node;
        if (!node.UiDefChilds) return undefined;
        for (const childKey of Object.keys(node.UiDefChilds)) {
            const found = findNodeWithKey(node.UiDefChilds[childKey]);
            if (found) return found;
        }
        return undefined;
    };

    // If we reached a child path, prefer to operate inside that subtree
    if (matchedChild && current) {
        const target = findNodeWithKey(current);
        if (target) {
            operateOnNode(target);
            // When explicit child path was provided, remove root occurrence to match tests
            if (newObj.UiDefValues && Object.prototype.hasOwnProperty.call(newObj.UiDefValues, firstKey)) {
                delete newObj.UiDefValues[firstKey];
            }
            return newObj;
        }
        // if not found deeper, operate at deepest reached node (create array on add)
        const did = operateOnNode(current);
        if (did) {
            if (newObj.UiDefValues && Object.prototype.hasOwnProperty.call(newObj.UiDefValues, firstKey)) {
                delete newObj.UiDefValues[firstKey];
            }
        }
        return newObj;
    }

    // If root contains the array, operate there
    if (newObj.UiDefValues && Object.prototype.hasOwnProperty.call(newObj.UiDefValues, firstKey)) {
        operateOnNode(newObj);
        return newObj;
    }

    // If we matched a child but it didn't have the array, add/operate at deepest reached node
    if (matchedChild && current) {
        if (!current.UiDefValues) current.UiDefValues = {};
        // If adding (keyValue null and changeKey == '.') create array and add
        if (keyValue === null && changeKey === '.' && changeValue !== null) {
            current.UiDefValues[firstKey] = Array.isArray(current.UiDefValues[firstKey]) ? [...current.UiDefValues[firstKey], changeValue] : [changeValue];
            return newObj;
        }

        // If not adding and no array exists, nothing to update
        return newObj;
    }

    // If nothing matched, but root doesn't have UiDefValues[firstKey] and operation is add, create at root
    if ((!newObj.UiDefValues || !Object.prototype.hasOwnProperty.call(newObj.UiDefValues, firstKey)) && keyValue === null && changeKey === '.' && changeValue !== null) {
        if (!newObj.UiDefValues) newObj.UiDefValues = {};
        newObj.UiDefValues[firstKey] = [changeValue];
        return newObj;
    }

    return newObj;
};

export function InitActionsFromUiDefinition(
    storeDataChildNames: string[],
    uiComponents: UiComponent[]
): UiActionStore[] {

    var path:string = storeDataChildNames ? storeDataChildNames.join('.') : '';
    var actions = GetActionsFromUiDefinition(uiComponents);

    return actions.map((action) => ({
        ...action,
        path: path,
    }));
}

export function GetActionsFromUiDefinition(
    uiComponents: UiComponent[]
): UiAction[] {

    // Rekursive Funktion zum Vereinfachen der UiComponents und Extrahieren von UiActions
    const flatElements = (array: UiComponent[]) => {
        const traverse = (subArray: UiComponent[], acc: UiAction[] = []) => {
            if (subArray && Array.isArray(subArray)) {
                subArray.forEach((element) => {
                    // Wenn Objekt mit bestimmter Bedingung es zum Ergebnis-acc hinzufügen
                    if (element.type.toLowerCase() === 'action') {
                        acc.push(element as UiAction);
                    }
                    // Wenn untergeordnete Elemente vorhanden sind, durchlaufe sie tief
                    if (element.children) {
                        traverse(element.children, acc);
                    }
                });
            }
            // Akkumulator zurückgeben
            return acc;
        };
        return traverse(array, []);
    };

    if (!uiComponents) return [];
    return flatElements(uiComponents as UiComponent[]);
}


/**
 * Aktualisiert eine Eigenschaft in einem verschachtelten Objekt mit einer Punkt-Notation-Pfadzeichenfolge.
 * Erstellt ein neues Objekt mit der aktualisierten Eigenschaft und hinterläßt das ursprüngliche Objekt unverändert.
 * 
 * @param obj - Das zu aktualisierende Objekt. Wenn null oder undefined, wird ein leeres Objekt verwendet.
 * @param pathString - Eine Punkt-Notation-Pfadzeichenfolge, die angibt, wo der Wert gesetzt werden soll (z.B. "user.profile.name").
 * @param value - Der Wert, der im angegebenen Pfad gesetzt werden soll.
 * @returns Ein neues Objekt mit der aktualisierten Eigenschaft am angegebenen Pfad.
 * 
 * @example
 * const obj = { user: { name: "John" } };
 * const updated = updateObjectProperty(obj, "user.age", 30);
 * // Rückgabe: { user: { name: "John", age: 30 } }
 */
export function UpdateObjectProperty(
    obj: Record<string, any>,
    pathString: string,
    value: any
): Record<string, any> {
    if (!obj) obj = {};
    if (!pathString) return obj;
    if (pathString.length == 0) return obj;

    const path = pathString.split('.').filter(p => p);

    const newObj = { ...obj };
    let current = newObj;

    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
        }
        current[key] = { ...current[key] };
        current = current[key];
    }

    current[path[path.length - 1]] = value;
    return newObj;
}

/**
 * Liest eine Eigenschaft aus einem verschachtelten Objekt mit einer Punkt-Notation-Pfadzeichenfolge.
 * 
 * @param obj - Das zu durchsuchende Objekt.
 * @param pathString - Eine Punkt-Notation-Pfadzeichenfolge, die angibt, welcher Wert gelesen werden soll (z.B. "user.profile.name").
 * @returns Der Wert am angegebenen Pfad, oder undefined, wenn der Pfad nicht existiert.
 * 
 * @example
 * const obj = { user: { name: "John", age: 30 } };
 * const name = getObjectProperty(obj, "user.name");
 * // Rückgabe: "John"
 */
export function GetObjectProperty(
    obj: Record<string, any>,
    pathString: string
): any {
    if (!obj) return undefined;
    if (!pathString) return undefined;
    if (pathString.length == 0) return undefined;

    const path = pathString.split('.').filter(p => p);
    let current = obj;

    for (let i = 0; i < path.length; i++) {
        const key = path[i];
        if (!current[Object.keys(current)?.find(
            (c) => c.toLowerCase() === key.toLowerCase()
        )]
        ) {
            return undefined;
        }
        current = current[Object.keys(current)?.find(
            (c) => c.toLowerCase() === key.toLowerCase()
        )]
    }

    return current;
}

/**
 * Retrieves a value from a local store object using a dot-notation path.
 * 
 * @param obj - The store object to search within
 * @param pathString - A dot-notation path string (e.g., "parent.child.property")
 * @param storeDataChildNames - An array of child property names to search through in the store
 * @returns The value found at the specified path, or undefined if not found
 * 
 * @example
 * ```
 * const store = { data: { value: { nested: 'result' } } };
 * const result = getValueFromLocalStore(store, 'data.nested', ['data']);
 * // Returns the nested value
 * ```
 */
export function GetValueFromLocalStore(
    obj: Record<string, any>,
    pathString: string,
    storeDataChildNames: string[]
): any {
    // Ermittle den Wert aus dem lokalen Store basierend auf dem Pfadstring
    var pathStrings = pathString.split('.').filter(p => p);
    var storeValue = FindInStore(obj, pathStrings[0], storeDataChildNames);
    if (storeValue && typeof storeValue === 'object') {
        if (pathStrings.length > 1) {
            var remainingPath = pathStrings.slice(1).join('.');
            var result = GetObjectProperty(storeValue, remainingPath);
            return result;
        }
        else {
            return storeValue;
        }
    }
    return storeValue;
}

/**
 * Updates a value in a local store object at a specified path, returning a new deep-copied object.
 *
 * The function navigates through the provided object using the `pathString` and optionally through child levels
 * specified by `storeDataChildNames`. It updates the value at the target location without mutating the original object.
 *
 * @param obj - The source object representing the local store.
 * @param pathString - Dot-separated string specifying the path to the property to update (e.g., "foo.bar.baz").
 * @param storeDataChildNames - Array of child names to traverse through nested store levels.
 * @param newValue - The new value to set at the specified path.
 * @returns A new object with the updated value at the specified path.
 */
export function UpdateValueInLocalStore(
    obj: Record<string, any>,
    pathString: string,
    storeDataChildNames: string[],
    newValue: any
): any {
    // debug logs removed
    // Ermittle den Wert aus dem lokalen Store basierend auf dem Pfadstring

    if (!obj) return obj;

    const pathStrings = pathString.split('.').filter(p => p);
    const firstKey = pathStrings[0];

    // Create a deep copy to avoid mutating the original object
    const newObj = JSON.parse(JSON.stringify(obj));
    let current: any = newObj;

    // internal debug removed
    // Check if root contains the first key
    if (current.UiDefValues && Object.prototype.hasOwnProperty.call(current.UiDefValues, firstKey)) {
        // Update at root level
        if (pathStrings.length === 1) {
            current.UiDefValues[firstKey] = newValue;
        } else {
            const remainingPath = pathStrings.slice(1).join('.');
            current.UiDefValues[firstKey] = UpdateObjectProperty(current.UiDefValues[firstKey], remainingPath, newValue);
        }
        // internal debug removed
        return newObj;
    }

    // Otherwise, navigate through child levels
    if (!storeDataChildNames) storeDataChildNames = [];

    for (let i = 0; i < storeDataChildNames.length; i++) {
        const childName = storeDataChildNames[i];
        if (!current || !current.UiDefChilds) break;

        const matchingChildKey = Object.keys(current.UiDefChilds).find(
            (k) => k.toLowerCase() === (childName || '').toLowerCase()
        );

        if (!matchingChildKey) break;

        current = current.UiDefChilds[matchingChildKey];
        if (!current) break;

        // Check if the current level contains the key
        if (current.UiDefValues && Object.prototype.hasOwnProperty.call(current.UiDefValues, firstKey)) {
            if (pathStrings.length === 1) {
                current.UiDefValues[firstKey] = newValue;
            } else {
                const remainingPath = pathStrings.slice(1).join('.');
                current.UiDefValues[firstKey] = UpdateObjectProperty(current.UiDefValues[firstKey], remainingPath, newValue);
            }
            // internal debug removed
            return newObj;
        }
    }

    // If key not found, add it to the deepest level reached
    if (current && current.UiDefValues) {
        if (pathStrings.length === 1) {
            current.UiDefValues[firstKey] = newValue;
        } else {
            const remainingPath = pathStrings.slice(1).join('.');
            current.UiDefValues[firstKey] = UpdateObjectProperty({}, remainingPath, newValue);
        }
    }
    // internal debug removed

    return newObj;
}

/**
 * Retrieves a value from a nested store object based on a key and a path of child names.
 *
 * @param obj - The root store object containing `UiDefValues` and `UiDefChilds`.
 * @param key - The key to search for within `UiDefValues` at each level.
 * @param storeDataChildNames - An array of child names representing the path to traverse in `UiDefChilds`.
 * @returns The value associated with the specified key if found, otherwise `undefined`.
 *
 * @remarks
 * - If the key exists in the root object's `UiDefValues`, its value is returned immediately.
 * - Otherwise, the function traverses the `UiDefChilds` hierarchy following the provided path,
 *   searching for the key in each child's `UiDefValues`.
 * - The search is case-insensitive for child names.
 */
export function FindInStore(
    obj: Record<string, any>,
    key: string,
    storeDataChildNames: string[]
): any {
    // Ermittle den Wert aus dem lokalen Store basierend auf dem Pfadstring
    if (!obj) return undefined;

    // If root contains the value, return the raw value
    if (obj.UiDefValues && Object.prototype.hasOwnProperty.call(obj.UiDefValues, key)) {
        return obj.UiDefValues[key];
    }

    let current: any = obj;
    if (!storeDataChildNames || storeDataChildNames.length === 0) return undefined;

    for (let i = 0; i < storeDataChildNames.length; i++) {
        const childName = storeDataChildNames[i];
        if (!current || !current.UiDefChilds) break;

        const matchingChildKey = Object.keys(current.UiDefChilds).find(
            (k) => k.toLowerCase() === (childName || '').toLowerCase()
        );

        if (!matchingChildKey) break;

        current = current.UiDefChilds[matchingChildKey];
        if (!current) break;

        if (current.UiDefValues && Object.prototype.hasOwnProperty.call(current.UiDefValues, key)) {
            return current.UiDefValues[key];
        }
    }
    return undefined;

}

export function RemoveDataFromLocalStore(obj: LocalStoreEntry, propertyPath: string, path: string[]): LocalStoreEntry {
    if (!obj) return obj;

    const pathStrings = (propertyPath || '').split('.').filter(p => p);
    if (pathStrings.length === 0) return obj;

    const firstKey = pathStrings[0];
    const remainingPath = pathStrings.slice(1);

    // deep copy
    const newObj: LocalStoreEntry = JSON.parse(JSON.stringify(obj));

    // helper to remove nested property immutably (returns modified copy)
    const removeNested = (value: any, keys: string[]): any => {
        if (keys.length === 0) return value;
        if (value === null || value === undefined || typeof value !== 'object') return value;

        const key = keys[0];
        const copy = Array.isArray(value) ? value.slice() : { ...value };

        if (keys.length === 1) {
            if (Object.prototype.hasOwnProperty.call(copy, key)) {
                delete copy[key];
            }
            return copy;
        }

        copy[key] = removeNested(copy[key], keys.slice(1));
        return copy;
    };

    // If root contains the key, remove there (root takes precedence)
    if (newObj.UiDefValues && Object.prototype.hasOwnProperty.call(newObj.UiDefValues, firstKey)) {
        if (remainingPath.length === 0) {
            delete newObj.UiDefValues[firstKey];
        } else {
            newObj.UiDefValues[firstKey] = removeNested(newObj.UiDefValues[firstKey], remainingPath);
        }
        return newObj;
    }

    // Try to follow explicit path if provided
    let current: any = newObj;
    if (path && path.length > 0) {
        for (let i = 0; i < path.length; i++) {
            const childName = path[i];
            if (!current || !current.UiDefChilds) break;

            const matchingChildKey = Object.keys(current.UiDefChilds).find(
                (k) => k.toLowerCase() === (childName || '').toLowerCase()
            );

            if (!matchingChildKey) break;

            current = current.UiDefChilds[matchingChildKey];
            if (!current) break;

            if (current.UiDefValues && Object.prototype.hasOwnProperty.call(current.UiDefValues, firstKey)) {
                if (remainingPath.length === 0) {
                    delete current.UiDefValues[firstKey];
                } else {
                    current.UiDefValues[firstKey] = removeNested(current.UiDefValues[firstKey], remainingPath);
                }
                return newObj;
            }
        }
    }

    // If no explicit path or not found yet, search depth-first for the first occurrence and remove there
    const dfsRemove = (node: LocalStoreEntry): boolean => {
        if (!node) return false;
        if (node.UiDefValues && Object.prototype.hasOwnProperty.call(node.UiDefValues, firstKey)) {
            if (remainingPath.length === 0) {
                delete node.UiDefValues[firstKey];
            } else {
                node.UiDefValues[firstKey] = removeNested(node.UiDefValues[firstKey], remainingPath);
            }
            return true;
        }

        // Fallback: wenn das erste Segment nicht vorhanden ist, aber das nächste Segment direkt im UiDefValues existiert,
        // entferne dieses (z.B. propertyPath = 'key.value' und UiDefValues hat 'value').
        if (remainingPath.length > 0 && node.UiDefValues && Object.prototype.hasOwnProperty.call(node.UiDefValues, remainingPath[0])) {
            if (remainingPath.length === 1) {
                delete node.UiDefValues[remainingPath[0]];
            } else {
                node.UiDefValues[remainingPath[0]] = removeNested(node.UiDefValues[remainingPath[0]], remainingPath.slice(1));
            }
            return true;
        }

        if (!node.UiDefChilds) return false;

        for (const childKey of Object.keys(node.UiDefChilds)) {
            const child = node.UiDefChilds[childKey];
            if (dfsRemove(child)) return true;
        }

        return false;
    };

    dfsRemove(newObj);
    return newObj;
}

/**
 * Entfernt eine Eigenschaft aus einem verschachtelten Objekt mit einer Punkt-Notation-Pfadzeichenfolge.
 * Erstellt ein neues Objekt mit der gelöschten Eigenschaft und hinterläßt das ursprüngliche Objekt unverändert.
 * 
 * @param obj - Das zu bearbeitende Objekt.
 * @param pathString - Eine Punkt-Notation-Pfadzeichenfolge, die angibt, welche Eigenschaft gelöscht werden soll (z.B. "user.profile.name").
 * @returns Ein neues Objekt ohne die Eigenschaft am angegebenen Pfad.
 * 
 * @example
 * const obj = { user: { name: "John", age: 30 } };
 * const result = removeObjectProperty(obj, "user.name");
 * // Rückgabe: { user: { age: 30 } }
 */
export function RemoveObjectProperty(
    obj: Record<string, any>,
    pathString: string
): Record<string, any> {
    if (!obj) return obj;
    if (!pathString) return obj;
    if (pathString.length === 0) return obj;

    const path = pathString.split('.').filter(p => p);
    if (path.length === 0) return obj;

    // Deep copy
    const newObj = { ...obj };

    // If only one key, remove from root
    if (path.length === 1) {
        if (Object.prototype.hasOwnProperty.call(newObj, path[0])) {
            delete newObj[path[0]];
        }
        return newObj;
    }

    // Navigate to parent and remove the final key
    let current = newObj;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key] || typeof current[key] !== 'object') {
            // Path doesn't exist, return original
            return newObj;
        }
        // Create a shallow copy at each level
        current[key] = { ...current[key] };
        current = current[key];
    }

    // Remove the final key
    const finalKey = path[path.length - 1];
    if (Object.prototype.hasOwnProperty.call(current, finalKey)) {
        delete current[finalKey];
    }

    return newObj;
}